import { FC, useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import clsx from 'clsx'

import { BuyTicketApi } from 'shared/api/BuyTicket.ts'
import { DetailsAppeal } from 'shared/api/BuyTicketModel'
import {
  LIST_WORKING_STATUS,
  REQUEST_STATUS,
  TYPE_APPELS,
} from 'shared/constants'
import ErrorImage from 'shared/images/error.png'
import { getErrorMessage } from 'shared/lib/errorText'
import { startPaymentFromConfirmResult } from 'shared/lib/payment-start'
import { ButtonContinue } from 'shared/ui/bottom-continue'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'
import { HeaderGoBack } from 'shared/ui/header-go-back'
import { Loader } from 'shared/ui/loader'
import MBonusLine from 'shared/ui/mbonus/mbonus-line/mbonus-line'
import { Paper } from 'shared/ui/paper'
import { PopupContent } from 'shared/ui/poup-content'
import { TimerAppeal } from 'shared/ui/timer-appeal'

import styles from './styles.module.scss'

interface Props {
  state?: boolean
  data?: unknown
}

const AppealTicket: FC<Props> = () => {
  const { t } = useTranslation()

  const { closeDialog, openDialog } = useDialogBottomSheet()

  const { appealId } = useParams()

  const [loading, setLoading] = useState<boolean>(true)
  const [appealDetail, setAppealDetail] = useState<DetailsAppeal | null>(null)

  const getAppealDetail = async () => {
    if (appealId) {
      try {
        const { data: appeals } = await BuyTicketApi.getAppealInfo(appealId)

        const appealDetail = appeals?.data
          ?.filter((service) => {
            if (
              service.topic === TYPE_APPELS.EXCHANGE ||
              service.topic === TYPE_APPELS.REFUND
            ) {
              return service
            }
          })
          ?.at(0)

        if (appealDetail) setAppealDetail(appealDetail)
      } catch (error: unknown) {
      } finally {
        setLoading(false)
      }
    }
  }

  const onShowError = (errorMessage?: string) => {
    openDialog({
      child: (
        <PopupContent
          image={ErrorImage}
          title={t('error')}
          description={errorMessage || t('please_tey_again')}
          button={t('try_again')}
          onClick={closeDialog}
        />
      ),
      isDialog: false,
    })
  }

  const onGoToPay = async () => {
    setLoading(true)

    try {
      const { data } = await BuyTicketApi.buyTicketConfirm({
        id: appealDetail!.flight_appeal!.id,
        type: 'flight_appeals',
      })

      await startPaymentFromConfirmResult(data)
    } catch (err) {
      const errorText = getErrorMessage(err)

      onShowError(errorText)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getAppealDetail()
  }, [])

  const finishedTimer =
    appealDetail?.flight_appeal?.appeals_detail?.appeal_expire_date

  let hasTime: number = 0

  if (finishedTimer) {
    const now = new Date()
    hasTime = new Date(finishedTimer).getTime() - now.getTime()
  }

  return (
    <div className={clsx('container', styles.wrapper)}>
      <div className={styles.header}>
        <HeaderGoBack
          title={
            appealDetail?.topic === TYPE_APPELS.EXCHANGE
              ? t('feed_back.exchange_ticket')
              : t('feed_back.refund_ticket')
          }
          actionRight={
            finishedTimer ? (
              <TimerAppeal
                timeFinished={
                  appealDetail?.flight_appeal?.appeals_detail
                    ?.appeal_expire_date
                }
              />
            ) : null
          }
          buttonWhite
        />
      </div>

      {loading ? (
        <Loader marginTop={200} />
      ) : (
        <div>
          <div className={styles.contentTop}>
            <Paper
              padding={[11, 16]}
              radius={16}
            >
              <div className={styles.statusBar}>
                <p>{t('appeal_status')}</p>

                {appealDetail?.status ? (
                  <div>
                    {LIST_WORKING_STATUS?.includes(appealDetail?.status) && (
                      <p className={`${styles.statusItem} ${styles.pending}`}>
                        <span>{t('additional_names.pending')}</span>
                      </p>
                    )}

                    {REQUEST_STATUS.CLOSED === appealDetail?.status ? (
                      <p className={`${styles.statusItem} ${styles.open}`}>
                        <span>{t('additional_names.open')}</span>
                      </p>
                    ) : null}

                    {REQUEST_STATUS.CANCELLED === appealDetail?.status ? (
                      <p className={`${styles.statusItem} ${styles.rejected}`}>
                        <span>{t('additional_names.cancelled')}</span>
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </Paper>

            {appealDetail?.flight_appeal?.appeals_detail?.commentary ? (
              <Paper
                padding={[16]}
                radius={16}
              >
                <div className={styles.content}>
                  {appealDetail?.flight_appeal?.appeals_detail?.commentary}
                </div>
              </Paper>
            ) : null}
          </div>

          <div className={styles.priceTop}>
            <Paper
              padding={[12, 16]}
              radius={16}
            >
              {REQUEST_STATUS.CANCELLED === appealDetail?.status ? (
                <div className={styles.price}>
                  <div>
                    <p>{t('rejected_appeal')}</p>
                  </div>
                </div>
              ) : null}

              {appealDetail?.topic === TYPE_APPELS.EXCHANGE && (
                <div className={styles.price}>
                  {parseInt(
                    appealDetail?.flight_appeal?.appeals_detail
                      ?.trade_price_amount || '0',
                  ) > 0 && (
                    <div>
                      <p>{t('cost_of_surcharge')}</p>
                      <p className={styles.price}>
                        {
                          appealDetail?.flight_appeal?.appeals_detail
                            ?.trade_price_amount
                        }{' '}
                        &#8384;
                      </p>
                    </div>
                  )}

                  {parseInt(
                    appealDetail?.flight_appeal?.appeals_detail
                      ?.penality_amount || '0',
                  ) > 0 ? (
                    <div>
                      <p>{t('amount_of_fine')}</p>
                      <p className={styles.price}>
                        {
                          appealDetail?.flight_appeal?.appeals_detail
                            ?.penality_amount
                        }{' '}
                        &#8384;
                      </p>
                    </div>
                  ) : null}
                </div>
              )}

              {appealDetail?.topic === TYPE_APPELS.REFUND &&
                parseInt(
                  appealDetail?.flight_appeal?.appeals_detail
                    ?.penality_amount || '0',
                ) > 0 && (
                  <div className={styles.price}>
                    <div>
                      <p>{t('amount_of_fine')}</p>
                      <p className={styles.price}>
                        {
                          appealDetail?.flight_appeal?.appeals_detail
                            ?.penality_amount
                        }{' '}
                        &#8384;
                      </p>
                    </div>
                  </div>
                )}
            </Paper>
            {parseInt(
              appealDetail?.flight_appeal?.appeals_detail?.penality_amount ||
                '0',
            ) > 0 ? (
              <Paper
                padding={[12, 16]}
                radius={16}
              >
                <div className={styles.totalPrice}>
                  <div className={styles.flexxer}>
                    <p>{t('amount_to_be_paid')}</p>
                    <p>
                      {`${
                        parseInt(
                          appealDetail?.flight_appeal?.appeals_detail
                            ?.penality_amount || '0',
                        ) +
                        parseInt(
                          appealDetail?.flight_appeal?.appeals_detail
                            ?.trade_price_amount || '0',
                        )
                      }`}{' '}
                      &#8384;
                    </p>
                  </div>

                  {appealDetail?.flight_appeal.bonus_amount && (
                    <MBonusLine
                      price={appealDetail.flight_appeal.bonus_amount}
                    />
                  )}
                </div>
              </Paper>
            ) : null}
          </div>
        </div>
      )}

      {hasTime < 0 ? (
        <p className={styles.textReCreate}>{t('recreate_appeal')}</p>
      ) : null}

      {appealDetail?.topic === TYPE_APPELS.EXCHANGE &&
      hasTime > 0 &&
      !LIST_WORKING_STATUS?.includes(appealDetail?.status) ? (
        <ButtonContinue
          disabled={loading}
          onClick={onGoToPay}
        >
          <span className={styles.text}>{t('to_pay')}</span>
        </ButtonContinue>
      ) : null}
    </div>
  )
}

export default AppealTicket
